import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import QRCode from "qrcode";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { SEO } from "@/components/SEO";
import { logger } from "@/utils/logger";

type Mode = "loading" | "enroll" | "challenge" | "recovery";
type Step =
  | "init"
  | "session-check"
  | "role-check"
  | "aal-check"
  | "factors-check"
  | "enrolling"
  | "ready-enroll"
  | "ready-challenge"
  | "verifying"
  | "done";

const STEP_LABEL: Record<Step, string> = {
  "init": "Initialisiere…",
  "session-check": "Session wird geprüft…",
  "role-check": "Admin-Rolle wird geprüft…",
  "aal-check": "MFA-Status wird ermittelt…",
  "factors-check": "MFA-Faktoren werden geladen…",
  "enrolling": "TOTP-Faktor wird erstellt…",
  "ready-enroll": "MFA wird eingerichtet – QR-Code scannen",
  "ready-challenge": "Code eingeben",
  "verifying": "Code wird geprüft…",
  "done": "Fertig",
};

const AdminMfa = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { toast } = useToast();

  const next = (() => {
    const n = params.get("next");
    return n && n.startsWith("/") && !n.startsWith("//") ? n : "/intern";
  })();

  const [mode, setMode] = useState<Mode>("loading");
  const [step, setStep] = useState<Step>("init");
  const [factorId, setFactorId] = useState<string | null>(null);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [recoveryReason, setRecoveryReason] = useState<string | null>(null);

  // Ref-Guard: Init darf pro Mount nur EINMAL laufen (verhindert doppelte enroll/unenroll
  // durch React-StrictMode-Doppelmounts, Toast-Identitätswechsel oder Re-Renders).
  const initStartedRef = useRef(false);

  useEffect(() => {
    if (initStartedRef.current) return;
    initStartedRef.current = true;

    const enterRecovery = (reason: string) => {
      setRecoveryReason(reason);
      setMode("recovery");
    };

    (async () => {
      try {
        setStep("session-check");
        const { data: sess } = await supabase.auth.getSession();
        if (!sess.session) {
          // Nur bei fehlender Session: automatisch zurück zum Login (reauth).
          navigate(
            `/auth?reauth=1&next=${encodeURIComponent(`/admin-mfa?next=${next}`)}`,
            { replace: true }
          );
          return;
        }

        setStep("role-check");
        const { data: hasAdmin, error: roleErr } = await supabase.rpc("has_role", {
          _user_id: sess.session.user.id,
          _role: "admin",
        });
        if (roleErr) {
          logger.error("AdminMfa: role check failed", roleErr);
          enterRecovery(
            `Admin-Rollenprüfung fehlgeschlagen: ${roleErr.message}. ` +
              "Bitte Session zurücksetzen und erneut anmelden."
          );
          return;
        }
        if (!hasAdmin) {
          enterRecovery(
            "Dieser Account hat keine Admin-Rolle. Bitte mit dem Admin-Konto neu anmelden."
          );
          return;
        }

        setStep("aal-check");
        const { data: aal, error: aalErr } =
          await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (aalErr) {
          logger.error("AdminMfa: getAAL failed", aalErr);
          enterRecovery(`MFA-Status konnte nicht ermittelt werden: ${aalErr.message}.`);
          return;
        }
        if (aal?.currentLevel === "aal2") {
          setStep("done");
          navigate(next, { replace: true });
          return;
        }

        setStep("factors-check");
        const { data: factors, error } = await supabase.auth.mfa.listFactors();
        if (error) {
          logger.error("mfa.listFactors error", error);
          enterRecovery(`MFA-Faktoren konnten nicht geladen werden: ${error.message}.`);
          return;
        }

        const verified = factors?.totp?.find((f) => f.status === "verified");
        if (verified) {
          setFactorId(verified.id);
          setMode("challenge");
          setStep("ready-challenge");
          return;
        }

        // Kein verifizierter Faktor: einen (evtl. hängenden) unverified aufräumen und neu enrollen.
        setStep("enrolling");
        const unverified = factors?.totp?.find((f) => f.status !== "verified");
        if (unverified) {
          try {
            await supabase.auth.mfa.unenroll({ factorId: unverified.id });
          } catch (e) {
            logger.error("mfa.unenroll (stale) failed", e);
          }
        }
        const { data: enroll, error: eErr } =
          await supabase.auth.mfa.enroll({ factorType: "totp" });
        if (eErr) throw eErr;
        const dataUrl = await QRCode.toDataURL(enroll.totp.uri, { margin: 1, width: 220 });
        setFactorId(enroll.id);
        setSecret(enroll.totp.secret);
        setQr(dataUrl);
        setMode("enroll");
        setStep("ready-enroll");
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        logger.error("AdminMfa init error", e);
        enterRecovery(`MFA-Einrichtung fehlgeschlagen: ${msg}.`);
      }
    })();
    // Absichtlich leere Dependencies – Init läuft genau einmal pro Mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetSessionAndReauth = async () => {
    try {
      await supabase.auth.signOut();
    } catch (e) {
      logger.error("signOut error", e);
    }
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith("sb-") || k.includes("supabase"))
        .forEach((k) => localStorage.removeItem(k));
      sessionStorage.clear();
    } catch {
      /* ignore */
    }
    window.location.href = `/auth?reauth=1&next=${encodeURIComponent("/intern")}`;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!factorId || code.length !== 6) return;
    setBusy(true);
    setStep("verifying");
    try {
      let currentChallengeId = challengeId;
      if (!currentChallengeId) {
        const { data: ch, error } = await supabase.auth.mfa.challenge({ factorId });
        if (error) throw error;
        currentChallengeId = ch.id;
        setChallengeId(ch.id);
      }
      const { error: vErr } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: currentChallengeId!,
        code,
      });
      if (vErr) throw vErr;
      toast({ title: "MFA erfolgreich", description: "Zwei-Faktor-Authentifizierung bestätigt." });
      setStep("done");
      navigate(next, { replace: true });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: "Code ungültig", description: msg, variant: "destructive" });
      setChallengeId(null);
      setCode("");
      setStep(mode === "enroll" ? "ready-enroll" : "ready-challenge");
    } finally {
      setBusy(false);
    }
  };

  const showStatus = import.meta.env.DEV || import.meta.env.MODE !== "production";

  return (
    <>
      <SEO title="Zwei-Faktor-Authentifizierung" description="Admin MFA." noindex />
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <ShieldCheck className="w-6 h-6 text-primary" />
            </div>
            <CardTitle>
              {mode === "enroll"
                ? "MFA einrichten"
                : mode === "recovery"
                ? "Anmeldung nicht möglich"
                : mode === "challenge"
                ? "Zwei-Faktor-Code"
                : "Zwei-Faktor-Authentifizierung"}
            </CardTitle>
            <CardDescription>
              {mode === "enroll"
                ? "Scannen Sie den QR-Code mit Google Authenticator (oder einer anderen TOTP-App wie 1Password, Authy) und geben Sie den 6-stelligen Code ein."
                : mode === "recovery"
                ? "Der Admin-Zugang konnte nicht initialisiert werden."
                : mode === "challenge"
                ? "Bitte geben Sie den 6-stelligen Code aus Google Authenticator ein."
                : "Bitte warten…"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {showStatus && (
              <div className="mb-4 text-center text-xs text-muted-foreground border border-dashed border-border rounded px-3 py-2">
                Schritt: {STEP_LABEL[step]}
              </div>
            )}

            {mode === "loading" && (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            )}

            {mode === "recovery" && (
              <div className="space-y-4">
                {recoveryReason && (
                  <p className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded p-3">
                    {recoveryReason}
                  </p>
                )}
                <Button className="w-full" onClick={resetSessionAndReauth}>
                  Session zurücksetzen &amp; neu anmelden
                </Button>
              </div>
            )}

            {mode === "enroll" && qr && (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <img src={qr} alt="MFA QR-Code" className="rounded border" width={220} height={220} />
                </div>
                {secret && (
                  <div className="text-center space-y-1">
                    <p className="text-xs text-muted-foreground">
                      Falls der QR-Code nicht scannbar ist, in Google Authenticator „Einrichtungsschlüssel eingeben“ wählen und dieses Secret einfügen:
                    </p>
                    <code className="text-xs break-all font-mono select-all">{secret}</code>
                  </div>
                )}
              </div>
            )}

            {(mode === "enroll" || mode === "challenge") && (
              <form onSubmit={submit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="code">6-stelliger Code</Label>
                  <Input
                    id="code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="123456"
                    className="text-center tracking-widest text-lg"
                  />
                </div>
                <Button type="submit" className="w-full" disabled={busy || code.length !== 6}>
                  {busy ? "Prüfe..." : "Bestätigen"}
                </Button>
              </form>
            )}

            <div className="mt-6 pt-4 border-t">
              <Button variant="ghost" className="w-full" onClick={() => navigate("/")}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Zurück zur Startseite
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default AdminMfa;
